import type { IndustryPage } from '../industries';

const lines = [
  { item: 'Exterior glass, 32 windows on a two-storey house, water-fed pole', amount: '$256' },
  { item: 'Interior glass, same 32 windows, sills wiped', amount: '$192' },
  { item: 'Screens removed, washed and refitted; tracks vacuumed and wiped', amount: '$96' },
  { item: 'Optional: hard-water stain treatment on four lower panes by the sprinkler', amount: '$80' },
];

const faq = [
  {
    q: 'Do window cleaners price per window or per pane?',
    a: 'Both are common. Per window is easier to explain to homeowners; per pane is fairer on divided-light windows. Whichever you use, separate outside from inside and list screens, tracks and stain removal as extras.',
  },
  {
    q: 'What do I say about scratches and hard-water stains?',
    a: 'That existing scratches stay, and that mineral stains may only partly come off. Put it in your terms so it is on the page the customer signs, rather than something you explain on the ladder.',
  },
  {
    q: 'Can a homeowner choose interior cleaning after seeing the exterior price?',
    a: 'Yes. Make interior, screens or stain treatment optional lines. On the quote page the homeowner ticks what they want and the total updates before they sign.',
  },
  {
    q: 'Does Scopie run recurring rounds or plan routes?',
    a: 'No, not yet. There are no automatic rounds, route planning or recurring billing in Scopie. It is built for quoted jobs: one-off cleans, move-ins and commercial work that is priced, signed, scheduled and invoiced.',
  },
];

const page: IndustryPage = {
  slug: 'window-cleaning',
  order: 9,
  group: 'Cleaning',
  path: '/for/window-cleaning',
  primaryKeyword: 'window cleaning business software',
  secondaryKeywords: ['window cleaning quote template', 'how to price window cleaning'],
  title: 'Window Cleaning Quotes by Pane and Side | Scopie', // 48
  metaDescription:
    'Price window cleaning by window or pane, inside and out, with screens, tracks and stain removal as extras the homeowner can tick before signing.', // 145
  h1: 'Window cleaning quotes with inside, outside and extras spelled out',
  answerFirst:
    'Scopie gives window cleaners a quote that separates exterior and interior glass, counts windows or panes, and lists screens, tracks and hard-water treatment as extras the customer can tick. Commercial clients can forward the link to whoever approves the work, and every signed job lands on your schedule with the right gear.',
  exampleQuote: { label: 'Sample window cleaning quote', lines, total: '$624', depositNote: 'Weather and access terms are the cleaner\'s own clauses.' },
  blocks: [
    {
      type: 'sample-quote',
      id: 'sample',
      h2: 'Thirty-two windows, priced the way you count them',
      label: 'Sample quote: two-storey house, inside and out',
      lines,
      total: '$624',
      totalLabel: 'Total with stain treatment',
      note: 'Example figures. The stain treatment is optional and can be ticked or skipped by the homeowner.',
    },
    {
      type: 'text',
      id: 'whole-house',
      h2: '"Whole house" is not a price',
      lead: 'They assumed the screens were included. You assumed they were not.',
      paragraphs: [
        'A flat price for the house hides the question every homeowner asks later: was that inside too? Splitting glass by side, and screens and tracks into their own lines, turns that conversation into a choice on the quote rather than a disagreement on the driveway.',
        'It also makes upselling honest. The homeowner who called for outside-only sees exactly what inside would add, and can tick it there and then.',
      ],
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Where window quotes go wrong',
      style: 'mistakes',
      items: [
        { title: 'Storeys and access left out', text: 'A third-floor bay or a window over a conservatory roof takes longer. Note the height and how you reach it.' },
        { title: 'No word on scratches or failed seals', text: 'Fogged double glazing will not clean up. Say so before you start.' },
        { title: 'French panes counted as one window', text: 'Divided lights take several times as long. Count panes on those windows.' },
        { title: 'Weather days unplanned', text: 'Rain and high wind move outside work. Write what happens to the date and the price.' },
        { title: 'Post-construction mess priced as a normal clean', text: 'Paint, stickers and mortar on new glass need scrapers and time. Quote it as a separate line.' },
      ],
    },
    {
      type: 'text',
      id: 'commercial',
      h2: 'Storefronts and offices want something to forward',
      paragraphs: [
        'Property managers rarely approve spend on the spot. Because every quote is a link with your business name at the top, they can send it upstairs, and the person who approves it can sign it. Describe the agreed visit frequency in the scope so the building and your crew read the same thing.',
        'Signed work goes on the schedule with the crew and the equipment: the long ladder, the water-fed pole, the lift if a job needs one. Your crew sees the address and the time; the price stays in the office.',
      ],
    },
    {
      type: 'list',
      id: 'season',
      h2: 'The window cleaning year',
      style: 'bullets',
      items: [
        { title: 'Spring', text: 'Pollen, winter grime and the first big wave of residential calls.' },
        { title: 'Summer', text: 'Storefronts, move-ins and post-construction cleans.' },
        { title: 'Fall', text: 'Pre-holiday cleans and screens off before winter.' },
        { title: 'Winter', text: 'Interior work and commercial contracts carry the calendar.' },
      ],
    },
    { type: 'faq', id: 'faq', h2: 'Window cleaning quotes: what people ask', items: faq },
  ],
  sections: [],
  faq,
  internalLinks: [
    { href: '/for', anchor: 'All industries', status: 'live' },
    { href: '/pricing', anchor: 'Scopie plans and pricing', status: 'live' },
    { href: '/for/pressure-washing', anchor: 'Scopie for pressure washing businesses', status: 'live' },
    { href: '/for/gutter-cleaning', anchor: 'Scopie for gutter businesses', status: 'live' },
    { href: '/templates/window-cleaning-quote-template', anchor: 'free window cleaning quote template', status: 'planned' },
  ],
  cta: {
    heading: 'Quote the next house by the pane',
    text: 'Add your per-window prices and extras to Scopie once, and send spring quotes before the pollen settles.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
  claimsToVerify: [],
};

export default page;
